// Intelligent satsang schedule generator.
// Generates multiple distinct variants honoring proximity groups, no same-day double duty,
// rotation (last-3-month history), load balancing, CD/LCD placement and Special SK locks.

import { GHARS, PATHIS as ALL_PATHIS, PREACHERS as ALL_PREACHERS, personTitle, isDefaultArea } from "../data/rssbData";

// Sewadars on leave for the month being generated are removed from these pools.
let PATHIS = ALL_PATHIS;
let PREACHERS = ALL_PREACHERS;
export const setLeave = (ids = []) => {
  const off = new Set(ids);
  PATHIS = ALL_PATHIS.filter((p) => !off.has(p.id));
  PREACHERS = ALL_PREACHERS.filter((p) => !off.has(p.id));
};
import { getHistory } from "./storage";

// seeded RNG (mulberry32)
function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const shuffle = (arr, rand) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Area-specific sewadar placement rules (Area Secretary instructions).
const BANKATI_GHARS = ["g21", "g22", "g23", "g24"];
const NEAR_RAGHAV = ["g10", "g11", "g25"];
const MAJHRA_SET = ["g6", "g7", "g1"];
const PERSON_GHARS = {
  p15: ["g7"],
  p17: MAJHRA_SET, p19: MAJHRA_SET,
  p24: NEAR_RAGHAV, p25: NEAR_RAGHAV, p26: NEAR_RAGHAV, p27: NEAR_RAGHAV, p28: NEAR_RAGHAV,
  p61: NEAR_RAGHAV, p62: NEAR_RAGHAV,
  s18: ["g12", "g13", "g14", "g15"],
};
// A-grade pathis — only these go with visiting (special) satsang kartas.
const A_GRADE_PALIA = ["p2", "p6", "p60", "p50", "p9", "p33", "p45", "p48", "p58", "p8", "p15", "p34", "p35"];
const A_GRADE = new Set(isDefaultArea ? A_GRADE_PALIA : []);
const isFemale = (p) => ["श्रीमती", "कुमारी"].includes(personTitle(p));
const norm = (s) => (s || "").replace(/[\sािीुूंँ़–-]/g, "");
const ownGharId = (p) => GHARS.find((g) => norm(g.hi) === norm(p.placeHi))?.id;

export function allowedAt(p, gharId) {
  if (!isDefaultArea) return true; // area-specific rules apply to Palia Kalan only
  if (PERSON_GHARS[p.id]) return PERSON_GHARS[p.id].includes(gharId);
  if (p.role) return true; // readers: any satsang ghar
  const own = ownGharId(p);
  if (isFemale(p) && BANKATI_GHARS.includes(own)) return own === gharId;
  return true;
}

// Pathis for one visiting SK's satsangs on a day: A-grade only, nearest (own ghar > own center group)
// and least used first; one pathi travels with the SK unless a ghar's own A-grade pathi has no special sewa yet.
const gharGroup = (id) => GHARS.find((g) => g.id === id)?.group;
const nearScore = (p, gharId) => (ownGharId(p) === gharId ? 0 : p.group === gharGroup(gharId) ? 1 : 3);
function planSpecialPathis(gharIds, used, spLoad, rand) {
  const ids = gharIds.filter(Boolean);
  const free = shuffle(PATHIS.filter((p) => !used.has(p.id)), rand);
  let pool = free.filter((p) => A_GRADE.has(p.id));
  if (!pool.length) pool = free.filter((p) => ids.every((g) => allowedAt(p, g)));
  if (!pool.length) pool = free;
  const out = {};
  if (!pool.length) return out;
  const cost = (p) => ids.reduce((s, g) => s + nearScore(p, g), 0) + (spLoad[p.id] || 0) * 4;
  const best = [...pool].sort((a, b) => cost(a) - cost(b))[0];
  ids.forEach((g) => {
    out[g] = best;
    if (ids.length < 2 || ownGharId(best) === g) return;
    const local = pool.find((p) => p.id !== best.id && !spLoad[p.id] && A_GRADE.has(p.id) && ownGharId(p) === g);
    if (local) out[g] = local;
  });
  Object.values(out).forEach((p) => (spLoad[p.id] = (spLoad[p.id] || 0) + 1 / ids.length));
  return out;
}

// Build the list of satsang dates for a month.
export function buildDates(year, month) {
  const dates = [];
  const d = new Date(year, month, 1);
  while (d.getMonth() === month) {
    const dow = d.getDay();
    if (dow === 0 || dow === 3 || dow === 4) {
      dates.push({
        iso: `${year}-${String(month + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
        day: d.getDate(),
        dow,
      });
    }
    d.setDate(d.getDate() + 1);
  }
  return dates;
}

// which ghars are active on a given weekday
const activeGhars = (dow) => GHARS.filter((g) => g.days.includes(dow));

function historyPairs() {
  const set = new Set();
  getHistory().forEach((h) => {
    if (h.preacherId && h.pathiId) set.add(`${h.gharId}|${h.preacherId}|${h.pathiId}`);
  });
  return set;
}

function pickPerson(candidates, ctx, rand, priority) {
  // candidates already filtered to same-group first; order by load then randomness
  const scored = candidates.map((c) => {
    const load = ctx.load[c.id] || 0;
    let s = load * 10; // lower load preferred
    s += rand() * (priority === "rotation" ? 6 : 3);
    return { c, s };
  });
  scored.sort((a, b) => a.s - b.s);
  return scored.length ? scored[0].c : null;
}

// Generate one variant with a given seed.
function generateVariant(year, month, dates, specialEntries, priority, seed) {
  const rand = rng(seed);
  const histSet = historyPairs();
  const cells = {}; // gharId -> {iso -> cell}
  GHARS.forEach((g) => (cells[g.id] = {}));

  // per-date used person ids (no same-day double duty)
  const usedByDate = {}; // iso -> Set
  const preacherLoad = {};
  const pathiLoad = {};
  dates.forEach((d) => (usedByDate[d.iso] = new Set()));

  const MAX_LOAD = 2; // management practice: a person gets at most ~2 (double) duties per month

  // 1. Place special SK locks first
  const spLoad = {};
  specialEntries.forEach((sp) => {
    const slots = [
      { gharId: sp.morningGharId, slot: "morning", time: "09:30" },
      { gharId: sp.noonGharId, slot: "noon", time: "13:00" },
    ];
    const plan = planSpecialPathis(slots.map((s) => s.gharId), usedByDate[sp.dateIso] || new Set(), spLoad, rand);
    slots.forEach(({ gharId, slot, time }) => {
      if (!gharId) return;
      const pathi = plan[gharId];
      if (!cells[gharId]) cells[gharId] = {};
      cells[gharId][sp.dateIso] = {
        type: "SPECIAL",
        preacherId: null,
        pathiId: pathi ? pathi.id : null,
        special: { name: sp.skName, fromCity: sp.fromCity, mobile: sp.skMobile, slot, time },
        locked: true,
      };
      if (pathi) {
        usedByDate[sp.dateIso]?.add(pathi.id);
        pathiLoad[pathi.id] = (pathiLoad[pathi.id] || 0) + 1;
      }
    });
  });

  // 2. Exactly one CD + one LCD (सवाल-जवाब) per ghar per month, SPREAD across dates
  //    so that few satsang ghars share the same CD/LCD day where flexibility allows.
  const dayTypeLoad = {};
  dates.forEach((d) => (dayTypeLoad[d.iso] = 0));
  const pickLeastLoadedDate = (avail, excludeIso) => {
    const pool = shuffle(avail.filter((d) => d.iso !== excludeIso), rand);
    pool.sort((a, b) => dayTypeLoad[a.iso] - dayTypeLoad[b.iso]);
    return pool[0] || null;
  };
  shuffle([...GHARS], rand).forEach((g) => {
    const avail = dates.filter((d) => g.days.includes(d.dow) && !cells[g.id][d.iso]);
    if (!avail.length) return;
    const cd = pickLeastLoadedDate(avail);
    if (cd) {
      cells[g.id][cd.iso] = { type: "CD", preacherId: null, pathiId: null };
      dayTypeLoad[cd.iso]++;
    }
    const lcd = pickLeastLoadedDate(avail, cd ? cd.iso : null);
    if (lcd && !g.noLcd) {
      cells[g.id][lcd.iso] = { type: "LCD", preacherId: null, pathiId: null };
      dayTypeLoad[lcd.iso]++;
    }
  });

  // same SR/SK is never scheduled again at the same ghar within the month
  const preacherAtGhar = {}; // gharId -> Set(preacherId)

  // 3a. Empty regular cells for every remaining active satsang
  const slots = [];
  dates.forEach((d) => {
    shuffle(activeGhars(d.dow), rand).forEach((g) => {
      if (cells[g.id][d.iso]) return; // special / CD / LCD already placed
      const c = { type: "SR", preacherId: null, pathiId: null };
      cells[g.id][d.iso] = c;
      slots.push({ g, d, c });
      preacherAtGhar[g.id] = preacherAtGhar[g.id] || new Set();
    });
  });

  const assign = (s, p, isPreacher) => {
    if (isPreacher) {
      s.c.preacherId = p.id;
      s.c.type = p.role;
      preacherAtGhar[s.g.id].add(p.id);
      preacherLoad[p.id] = (preacherLoad[p.id] || 0) + 1;
    } else {
      s.c.pathiId = p.id;
      pathiLoad[p.id] = (pathiLoad[p.id] || 0) + 1;
    }
    usedByDate[s.d.iso].add(p.id);
  };

  // 3b. Guarantee: every sewadar gets at least one sewa — most restricted people placed first
  [[PREACHERS, preacherLoad, true], [PATHIS, pathiLoad, false]].forEach(([people, load, isPreacher]) => {
    const field = isPreacher ? "preacherId" : "pathiId";
    const open = (p) =>
      slots.filter(
        (s) =>
          !s.c[field] &&
          allowedAt(p, s.g.id) &&
          !usedByDate[s.d.iso].has(p.id) &&
          (!isPreacher || !preacherAtGhar[s.g.id].has(p.id))
      );
    const idle = shuffle(people.filter((p) => !load[p.id]), rand)
      .map((p) => ({ p, n: open(p).length }))
      .sort((a, b) => a.n - b.n);
    idle.forEach(({ p }, idx) => {
      let opts = open(p);
      // restricted sewadar with no slot left: relax placement (nearest group first) only if
      // enough empty slots remain for everyone still waiting — never just displace another sewadar
      if (!opts.length) {
        const free = slots.filter((s) => !s.c[field] && !usedByDate[s.d.iso].has(p.id) && (!isPreacher || !preacherAtGhar[s.g.id].has(p.id)));
        const emptyCount = slots.filter((s) => !s.c[field]).length;
        const waiting = idle.slice(idx).filter(({ p: q }) => !load[q.id]).length;
        if (emptyCount >= waiting) {
          const near = free.filter((s) => s.g.group === p.group);
          opts = near.length ? near : free;
        }
      }
      if (opts.length) assign(opts[Math.floor(rand() * opts.length)], p, isPreacher);
    });
  });

  // 3c. Fill the rest — every satsang cell gets BOTH preacher and pathi
  slots.forEach((s) => {
    const { g, d, c } = s;
    const used = usedByDate[d.iso];
    const ok = (p) => !used.has(p.id) && allowedAt(p, g.id);
    if (!c.preacherId) {
      let cand = PREACHERS.filter((p) => ok(p) && !preacherAtGhar[g.id].has(p.id) && (preacherLoad[p.id] || 0) < MAX_LOAD);
      if (!cand.length) cand = PREACHERS.filter((p) => ok(p) && !preacherAtGhar[g.id].has(p.id));
      if (!cand.length) cand = PREACHERS.filter(ok);
      const pick = pickPerson(cand, { load: preacherLoad }, rand, priority);
      if (pick) assign(s, pick, true);
    }
    if (!c.pathiId) {
      // A-grade pathis are kept for special SKs; used in regular satsangs only as a last resort
      const reg = (p) => ok(p) && !A_GRADE.has(p.id);
      let cand = PATHIS.filter(
        (p) => reg(p) && (pathiLoad[p.id] || 0) < MAX_LOAD && (!c.preacherId || !histSet.has(`${g.id}|${c.preacherId}|${p.id}`))
      );
      if (!cand.length) cand = PATHIS.filter((p) => reg(p) && (pathiLoad[p.id] || 0) < MAX_LOAD);
      if (!cand.length) cand = PATHIS.filter(reg);
      if (!cand.length) cand = PATHIS.filter(ok);
      const pick = pickPerson(cand, { load: pathiLoad }, rand, priority);
      if (pick) assign(s, pick, false);
    }
  });

  // 4. Safety net — no cell may stay empty or half-filled (both SK and pathi always assigned)
  dates.forEach((d) => {
    const used = usedByDate[d.iso];
    activeGhars(d.dow).forEach((g) => {
      const c = cells[g.id][d.iso];
      if (!c || c.type === "CD" || c.type === "LCD" || c.type === "SPECIAL") return;
      if (!c.preacherId) {
        preacherAtGhar[g.id] = preacherAtGhar[g.id] || new Set();
        let cand = PREACHERS.filter((p) => !used.has(p.id) && !preacherAtGhar[g.id].has(p.id) && allowedAt(p, g.id));
        if (!cand.length) cand = PREACHERS.filter((p) => !used.has(p.id));
        const pick = pickPerson(cand, { load: preacherLoad }, rand, priority);
        if (pick) {
          c.preacherId = pick.id;
          c.type = pick.role;
          used.add(pick.id);
          preacherAtGhar[g.id].add(pick.id);
          preacherLoad[pick.id] = (preacherLoad[pick.id] || 0) + 1;
        }
      }
      if (!c.pathiId) {
        let cand = PATHIS.filter((p) => !used.has(p.id) && allowedAt(p, g.id));
        if (!cand.length) cand = PATHIS.filter((p) => !used.has(p.id));
        const pick = pickPerson(cand, { load: pathiLoad }, rand, priority);
        if (pick) {
          c.pathiId = pick.id;
          used.add(pick.id);
          pathiLoad[pick.id] = (pathiLoad[pick.id] || 0) + 1;
        }
      }
    });
  });

  const score = scoreVariant(cells, dates, preacherLoad, pathiLoad, histSet);
  const idle = [...PREACHERS.filter((p) => !preacherLoad[p.id]), ...PATHIS.filter((p) => !pathiLoad[p.id])].map((p) => p.id);
  score.percent = Math.max(0, score.percent - idle.length * 2);
  return { id: seed, cells, score, seed, preacherLoad, pathiLoad, idle };
}

function scoreVariant(cells, dates, preacherLoad, pathiLoad, histSet) {
  let total = 0,
    filled = 0,
    freshPairs = 0,
    pairCount = 0;

  Object.entries(cells).forEach(([gharId, ds]) => {
    Object.values(ds).forEach((c) => {
      total++;
      if (c.type === "CD" || c.type === "LCD" || c.type === "SPECIAL") {
        filled++;
        return;
      }
      if (c.preacherId && c.pathiId) filled++;
      if (c.preacherId && c.pathiId) {
        pairCount++;
        if (!histSet.has(`${gharId}|${c.preacherId}|${c.pathiId}`)) freshPairs++;
      }
    });
  });

  const coverage = total ? filled / total : 0;
  const rotation = pairCount ? freshPairs / pairCount : 1;

  const loads = [...Object.values(preacherLoad), ...Object.values(pathiLoad)];
  const mean = loads.length ? loads.reduce((a, b) => a + b, 0) / loads.length : 0;
  const variance = loads.length
    ? loads.reduce((a, b) => a + (b - mean) ** 2, 0) / loads.length
    : 0;
  const balance = mean ? Math.max(0, 1 - Math.sqrt(variance) / (mean + 1)) : 1;

  const overall = 0.5 * coverage + 0.3 * rotation + 0.2 * balance;
  return {
    percent: Math.round(overall * 100),
    coverage: Math.round(coverage * 100),
    rotation: Math.round(rotation * 100),
    balance: Math.round(balance * 100),
  };
}

export function generateSchedule(year, month, specialEntries, priority = "balanced", count = 3, leaveIds = []) {
  setLeave(leaveIds);
  const dates = buildDates(year, month);
  const variants = [];
  for (let i = 0; i < count; i++) {
    const seed = (year * 100 + month) * 1000 + i * 7919 + Math.floor(Math.random() * 5000);
    variants.push(generateVariant(year, month, dates, specialEntries, priority, seed));
  }
  variants.sort((a, b) => b.score.percent - a.score.percent);
  return { dates, variants, priority };
}

export function reshuffleVariant(year, month, specialEntries, priority) {
  const dates = buildDates(year, month);
  const seed = (year * 100 + month) * 1000 + Math.floor(Math.random() * 100000);
  return generateVariant(year, month, dates, specialEntries, priority, seed);
}


// Re-apply special SK locks onto an existing variant (when entries change after generation).
// Strips old locked SPECIAL cells, then places current entries with a free pathi.
export function overlaySpecials(variant, specialEntries) {
  const cells = {};
  Object.entries(variant.cells || {}).forEach(([g, ds]) => {
    cells[g] = {};
    Object.entries(ds).forEach(([iso, c]) => {
      if (!(c.locked && c.type === "SPECIAL")) cells[g][iso] = c;
    });
  });
  const spLoad = {};
  Object.values(cells).forEach((ds) =>
    Object.values(ds).forEach((c) => c?.type === "SPECIAL" && c.pathiId && (spLoad[c.pathiId] = (spLoad[c.pathiId] || 0) + 1))
  );
  (specialEntries || []).forEach((sp) => {
    if (!sp.dateIso) return;
    const slots = [
      { gharId: sp.morningGharId, slot: "morning", time: "09:30" },
      { gharId: sp.noonGharId, slot: "noon", time: "13:00" },
    ];
    const used = new Set();
    Object.entries(cells).forEach(([gid, ds]) => {
      const c = ds[sp.dateIso];
      if (c && !slots.some((s) => s.gharId === gid)) {
        if (c.pathiId) used.add(c.pathiId);
        if (c.preacherId) used.add(c.preacherId);
      }
    });
    const plan = planSpecialPathis(slots.map((s) => s.gharId), used, spLoad, Math.random);
    slots.forEach(({ gharId, slot, time }) => {
      if (!gharId) return;
      const pathi = plan[gharId];
      if (!cells[gharId]) cells[gharId] = {};
      cells[gharId][sp.dateIso] = {
        type: "SPECIAL",
        preacherId: null,
        pathiId: pathi ? pathi.id : null,
        special: { name: sp.skName, fromCity: sp.fromCity, mobile: sp.skMobile, slot, time },
        locked: true,
      };
    });
  });
  return { ...variant, cells };
}
